import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { v4 as uuidv4 } from 'uuid';

export interface Document {
  id: string;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  user_id: string;
  company_id?: string;
  is_approved: boolean | null;
  uploaded_at: string | null;
}

type Tables = Database['public']['Tables'];
type DocumentRow = Tables['investor_documents']['Row'];

/**
 * Service for handling document-related functionality
 */
export class DocumentService {
  /**
   * Upload a document to storage and create a database entry
   */
  static async uploadDocument(
    file: File,
    documentName: string,
    documentType: string,
    companyId?: string,
    assignedUserId?: string
  ): Promise<Document> {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        throw new Error('User not authenticated');
      }

      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        throw new Error('Failed to upload file');
      }

      // Create document record in the database
      const { data: document, error: dbError } = await supabase
        .from('investor_documents')
        .insert({
          document_name: documentName,
          document_type: documentType,
          file_path: fileName,
          file_size: file.size,
          user_id: assignedUserId || user.data.user.id,
          company_id: companyId,
          is_approved: false,
          uploaded_at: new Date().toISOString()
        } as Tables['investor_documents']['Insert'])
        .select()
        .single();

      if (dbError) {
        console.error('Error creating document record:', dbError);
        // Clean up the uploaded file if database insert fails
        await supabase.storage.from('documents').remove([fileName]);
        throw new Error('Failed to create document record');
      }

      return document as Document;
    } catch (error) {
      console.error('Error in uploadDocument:', error);
      throw error;
    }
  }

  /**
   * Get documents for a specific user
   */
  static async getDocuments(companyId?: string): Promise<Document[]> {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        throw new Error('User not authenticated');
      }

      // Get user's role
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.data.user.id)
        .single();

      let query = supabase
        .from('investor_documents')
        .select('*')
        .order('uploaded_at', { ascending: false });

      // If companyId is provided, filter by company
      if (companyId) {
        query = query.eq('company_id', companyId);
      } else if (profile?.user_type !== 'admin') {
        // If not admin, show only user's personal documents
        query = query.eq('user_id', user.data.user.id);
      }

      const { data: documents, error } = await query;

      if (error) {
        console.error('Error fetching documents:', error);
        throw new Error('Failed to fetch documents');
      }

      return (documents || []) as Document[];
    } catch (error) {
      console.error('Error in getDocuments:', error);
      throw error;
    }
  }

  /**
   * Delete a document
   */
  static async deleteDocument(documentId: string): Promise<void> {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        throw new Error('User not authenticated');
      }

      // Get the document to find its file path
      const { data: document, error: fetchError } = await supabase
        .from('investor_documents')
        .select('file_path')
        .eq('id', documentId)
        .single();

      if (fetchError || !document) {
        console.error('Error fetching document:', fetchError);
        throw new Error('Failed to fetch document');
      }

      // Delete the file from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([document.file_path]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        throw new Error('Failed to delete file from storage');
      }

      // Delete the document record from the database
      const { error: dbError } = await supabase
        .from('investor_documents')
        .delete()
        .eq('id', documentId);

      if (dbError) {
        console.error('Error deleting document record:', dbError);
        throw new Error('Failed to delete document record');
      }
    } catch (error) {
      console.error('Error in deleteDocument:', error);
      throw error;
    }
  }

  /**
   * Update a document
   */
  static async updateDocument(
    id: string,
    updates: Partial<Pick<Document, 'document_name' | 'document_type' | 'user_id'>>
  ): Promise<Document> {
    try {
      const { data, error } = await supabase
        .from('investor_documents')
        .update({
          ...updates,
          uploaded_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return data as Document;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  /**
   * Create a document record for an external link
   */
  static async createLinkDocument(
    title: string,
    link: string,
    userId: string
  ): Promise<Document> {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        throw new Error('User not authenticated');
      }

      // Create document record in the database
      const { data: document, error: dbError } = await supabase
        .from('investor_documents')
        .insert({
          document_name: title,
          document_type: 'link',
          file_path: link,
          file_size: 0,
          user_id: userId,
          is_approved: false,
          uploaded_at: new Date().toISOString()
        } as Tables['investor_documents']['Insert'])
        .select()
        .single();

      if (dbError) {
        console.error('Error creating document record:', dbError);
        throw new Error('Failed to create document record');
      }

      return document as Document;
    } catch (error) {
      console.error('Error in createLinkDocument:', error);
      throw error;
    }
  }
}
